(() => {
  if (customElements.get('chrissie-offer-buy')) return;
  let offerWritePending = false;

  class ChrissieOfferBuy extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.button = this.querySelector('[data-offer-add]');
      this.status = this.querySelector('[data-offer-status]');
      this.cartLink = this.querySelector('[data-offer-cart]');
      this.gift = this.querySelector('[data-offer-gift]');
      this.button?.addEventListener('click', () => this.addOffer());
    }

    rootUrl(path) {
      return `${window.Shopify?.routes?.root || '/'}${path.replace(/^\//, '')}`;
    }

    isActive() {
      const starts = Date.parse(this.dataset.startsAt);
      const ends = Date.parse(this.dataset.expiresAt);
      return Number.isFinite(starts) && Number.isFinite(ends) && Date.now() >= starts && Date.now() < ends;
    }

    message(text, state = '') {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.dataset.state = state;
      this.status.hidden = !text;
    }

    sectionIds(drawer) {
      return [...new Set(['cart-drawer', 'cart-counter', ...(drawer?.getSectionsToRender?.() || []).map(section => section.section)])].slice(0, 5);
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        clearTimeout(timer);
      }
    }

    async readCart() {
      const response = await this.request(this.rootUrl(`cart.js?_cc_offer=${Date.now()}`), {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Cart read failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Cart data incomplete');
      return cart;
    }

    async showCart(drawer, sections) {
      if (!drawer || typeof drawer.renderViewSections !== 'function') {
        window.location.assign(this.rootUrl('cart'));
        return;
      }
      const ids = this.sectionIds(drawer);
      let fresh = sections;
      const complete = () => fresh && ids.every(id => typeof fresh[id] === 'string' && fresh[id]);
      if (!complete()) {
        const query = new URLSearchParams({ sections: ids.join(','), _cc_offer_view: String(Date.now()) });
        const response = await this.request(`${this.rootUrl('cart')}?${query}`, {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: '*/*' }
        });
        if (!response.ok) throw new Error('Cart sections read failed');
        fresh = await response.json();
      }
      if (!complete()) throw new Error('Cart sections incomplete');
      drawer.cancelViewRefresh?.();
      drawer.renderViewSections(fresh);
      drawer.setActiveElement?.(this.button);
      drawer.open(this.button, false);
    }

    async addOffer() {
      if (!this.button || this.button.disabled || this.pending || offerWritePending || this.blocked) return;
      if (!this.isActive()) {
        this.message(this.dataset.expired, 'error');
        this.button.disabled = true;
        return;
      }
      const primary = Number(this.dataset.primaryVariant);
      const secondary = Number(this.gift?.value || this.dataset.secondaryVariant);
      if (!Number.isSafeInteger(primary) || primary <= 0 || !Number.isSafeInteger(secondary) || secondary <= 0) return;
      const drawer = document.querySelector('cart-drawer');
      const label = this.button.textContent;
      let writeAttempted = false;
      let writeSucceeded = false;
      this.pending = true;
      offerWritePending = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = this.dataset.adding;
      if (this.gift) this.gift.disabled = true;
      if (this.cartLink) this.cartLink.hidden = true;
      this.message('');
      drawer?.cancelViewRefresh?.();
      if (drawer) drawer.pendingQuantityUpdates = (drawer.pendingQuantityUpdates || 0) + 1;
      try {
        let items = [{ id: primary, quantity: 1 }, { id: secondary, quantity: 1 }];
        if (this.dataset.campaign === 'oct_barrier') {
          const cart = await this.readCart();
          const bought = product => cart.items.some(line => !line.parent_relationship && Number(line.product_id) === Number(product) && Number(line.quantity) > 0);
          const hasPrimary = bought(this.dataset.primaryProduct);
          const hasSecondary = bought(this.dataset.secondaryProduct);
          if (hasPrimary && hasSecondary) {
            this.message(this.dataset.already);
            if (this.cartLink) this.cartLink.hidden = false;
            try {
              await this.showCart(drawer);
            } catch (_) {
              window.location.assign(this.rootUrl('cart'));
            }
            return;
          }
          items = items.filter(item => item.id === primary ? !hasPrimary : !hasSecondary);
        }
        if (!this.isActive()) {
          this.blocked = true;
          this.message(this.dataset.expired, 'error');
          return;
        }
        const body = { items, sections: this.sectionIds(drawer), sections_url: window.location.pathname };
        writeAttempted = true;
        const response = await this.request(this.rootUrl('cart/add.js'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        const result = await response.json();
        if (!response.ok || result.status || result.errors) throw new Error('Offer add failed');
        writeSucceeded = true;
        this.message(this.dataset.added);
        if (this.cartLink) this.cartLink.hidden = false;
        try {
          await this.showCart(drawer, result.sections);
        } catch (_) {
          window.location.assign(this.rootUrl('cart'));
        }
        const detail = { source: 'chrissie-offer', campaign: this.dataset.campaign, cartData: result };
        try {
          document.dispatchEvent(new CustomEvent('chrissie:offer:added', { detail }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, detail);
          }
        } catch (_) {}
      } catch (_) {
        this.blocked = writeAttempted && !writeSucceeded;
        this.message(writeAttempted ? this.dataset.error : this.dataset.readError, 'error');
        if (this.cartLink) this.cartLink.hidden = false;
      } finally {
        if (drawer) drawer.pendingQuantityUpdates = Math.max(0, (drawer.pendingQuantityUpdates || 1) - 1);
        this.pending = false;
        offerWritePending = false;
        this.button.disabled = Boolean(this.blocked);
        this.button.removeAttribute('aria-busy');
        this.button.textContent = label;
        if (this.gift) this.gift.disabled = Boolean(this.blocked);
      }
    }
  }

  customElements.define('chrissie-offer-buy', ChrissieOfferBuy);
})();
